import '@assets/styles/app.scss';
import '@assets/styles/reset.scss';
import 'mapbox-gl/dist/mapbox-gl.css';
import 'react-lazy-load-image-component/src/effects/blur.css';
import 'react-quill/dist/quill.snow.css';
import 'simplebar-react/dist/simplebar.min.css';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import 'yet-another-react-lightbox/plugins/captions.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import 'yet-another-react-lightbox/styles.css';
import '../src/locales/i18n';

// ----------------------------------------------------------------------

import AuthCtx from '@auth/AuthCtx';
import NoSsr from '@components/cores/NoSsr';
import { HospitalCtx } from '@components/hospital';
import { LoadingApp } from '@components/LoadingApp';
import { CacheProvider, EmotionCache } from '@emotion/react';
import { LicenseInfo } from '@mui/x-data-grid-premium';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { persistor, useStore } from '@store';
import { NextPage } from 'next';
import App, { AppProps, AppContext } from 'next/app';
import Head from 'next/head';
import { Provider as ReduxProvider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { MotionLazyContainer } from '../src/components/animate';
import { StyledChart } from '../src/components/chart';
import ProgressBar from '../src/components/progress-bar';
import { SettingsProvider, ThemeSettings } from '../src/components/settings';
import SnackbarProvider from '../src/components/snackbar';
import ThemeLocalization from '../src/locales';
import ThemeProvider from '../src/theme';
import createEmotionCache from '../src/utils/createEmotionCache';

const clientSideEmotionCache = createEmotionCache();

type NextPageWithLayout = NextPage & {
  getLayout?: (page: React.ReactElement) => React.ReactNode;
};

interface MyAppProps extends AppProps {
  emotionCache?: EmotionCache;
  Component: NextPageWithLayout;
  hospitalId: number;
}

export default function MyApp(props: MyAppProps) {
  const { Component, emotionCache = clientSideEmotionCache, pageProps } = props;

  themes('medigo');

  const getLayout = Component.getLayout ?? ((page) => page);

  const { store } = useStore(props);

  try {
    LicenseInfo.setLicenseKey(
      'e0d9bb8070ce0054c9d9ecb6e82cb58fTz0wLEU9MzI0NzIxNDQwMDAwMDAsUz1wcmVtaXVtLExNPXBlcnBldHVhbCxLVj0y'
    );
  } catch (err) {
    console.info(err);
  }

  return (
    <>
      <Head>
        <meta name="viewport" content="initial-scale=1, width=device-width" />
      </Head>

      <ReduxProvider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          {() => (
            <NoSsr>
              <CacheProvider value={emotionCache}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <SettingsProvider>
                    <MotionLazyContainer>
                      <ThemeProvider>
                        <ThemeSettings>
                          <ThemeLocalization>
                            <SnackbarProvider>
                              <StyledChart />
                              <ProgressBar />
                              <AuthCtx.Provider>
                                <HospitalCtx.Provider>
                                  {getLayout(<Component {...pageProps} />)}
                                </HospitalCtx.Provider>
                              </AuthCtx.Provider>
                            </SnackbarProvider>
                          </ThemeLocalization>
                        </ThemeSettings>
                      </ThemeProvider>
                    </MotionLazyContainer>
                  </SettingsProvider>
                </LocalizationProvider>
              </CacheProvider>
            </NoSsr>
          )}
        </PersistGate>
      </ReduxProvider>
      <LoadingApp />
    </>
  );
}

MyApp.getInitialProps = async (context: AppContext) => {
  const appProps = await App.getInitialProps(context);
  return { ...appProps };
};

const themes = (key: string) => {
  switch (key) {
    default:
      import('@assets/styles/medigo.theme.scss' as string);
      break;
  }
};
